import { useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';

import Logo from '@webclient/components/logo';
import Carousel from '@webclient/components/user-authentication/components/carousel';
import useUserAuth from '@webclient/components/user-authentication/hooks/useUserAuth';
import { StyledIconButton } from '@webclient/components/user-authentication/sections/modal-stepper/desktop/styles';
import ModalWrapper from '@webclient/components/user-authentication/sections/modal-stepper/ModalWrapper';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useQueryParams from '@webclient/hooks/useQueryParams';
import useWorkspace from '@webclient/hooks/useWorkspace';

import { setAttemptedToLogin } from '@webclient/state/slices/auth';

import KnowledgeBaseIcon from '~icons/knowz-iconify/knowledge-base';
import LiveIcon from '~icons/knowz-iconify/live';
import SearchSmartIcon from '~icons/knowz-iconify/search-smart';

import { Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

const MODAL_WIDTH = 848;
const MODAL_HEIGHT = 640;

const IMAGE_PLACEHOLDER_WIDTH = 360;

export default function ModalStepper({ children }: React.PropsWithChildren) {
  const { t } = useTranslate();
  const { removeQueryParamByKey } = useQueryParams();
  const theme = useTheme();
  const { resetForm } = useUserAuth();
  const dispatch = useAppDispatch();
  const { resetWorkspaceToIndividualRole } = useWorkspace();

  const cards = useMemo(
    () => [
      {
        title: t('layout.auth.gallery.search.title', 'Smarter Search'),
        description: t(
          'layout.auth.gallery.search.description',
          'Search your own knowledge & the web more intelligently with advanced ai integrations',
        ),
        image: `https://assets.knowz.com/assets/images/login/search-${theme.palette.mode}.png`,
        Icon: SearchSmartIcon,
      },
      {
        title: t('layout.auth.gallery.live.title', 'Knowz Live'),
        description: t(
          'layout.auth.gallery.live.description',
          'Knowz Live is a powerful ai powered meeting companion with access to all your teams Knowledge',
        ),
        image: `https://assets.knowz.com/assets/images/login/live-${theme.palette.mode}.png`,
        Icon: LiveIcon,
      },
      {
        title: t('layout.auth.gallery.knowledge.title', 'Knowledge Bases'),
        description: t(
          'layout.auth.gallery.knowledge.description',
          'Train custom versions of Knowz AI with Knowledge Bases & collaborate with teams of up to 100',
        ),
        image: `https://assets.knowz.com/assets/images/login/knowledge-${theme.palette.mode}.png`,
        Icon: KnowledgeBaseIcon,
      },
    ],
    [t, theme.palette.mode],
  );

  function closeModal() {
    resetForm();
    dispatch(setAttemptedToLogin(true));
    removeQueryParamByKey('action');
    resetWorkspaceToIndividualRole();
  }

  return (
    <ModalWrapper
      width={MODAL_WIDTH}
      height={MODAL_HEIGHT}
    >
      <StyledIconButton
        color="secondary"
        variant="outlined"
        onClick={closeModal}
      >
        <CloseIcon />
      </StyledIconButton>

      <Stack sx={{ p: 8, width: '100%' }}>
        {/* TODO: this second Stack is a workaround for aligning the logo. Remove once the logo is trimmed */}
        <Stack
          alignItems="flex-start"
          sx={{ marginInlineStart: '-9px' }}
        >
          <Logo />
        </Stack>

        {children}
      </Stack>

      <Carousel
        cards={cards.map(({ Icon, image, title, description }, index) => (
          <Stack
            width="100%"
            key={`card-${index}`}
            gap={4}
          >
            <img
              src={image}
              alt="placeholder"
              width={IMAGE_PLACEHOLDER_WIDTH}
              loading="lazy"
            />

            <Stack
              gap={2}
              alignItems="center"
            >
              <Stack
                gap={2}
                direction="row"
                alignItems="center"
              >
                <Icon
                  fontSize={25}
                  color={theme.palette.utility.gray700}
                />

                <Text
                  variant="displayXs"
                  weight="semibold"
                >
                  {title}
                </Text>
              </Stack>

              <Text
                variant="textSm"
                align="center"
              >
                {description}
              </Text>
            </Stack>
          </Stack>
        ))}
      />
    </ModalWrapper>
  );
}
